import classNames from "classnames";
import { useState } from "react";
import { JsonViewer } from "@textea/json-viewer";
import {
	getTiersWithoutPrices,
	getRawProductWithoutPrices,
} from "../../../../../../common/premium_tiers";
import { DiscordEntitlementEntity } from "../../../../../../src/entity/DiscordEntitlement";
import { StripeEntitlementEntity } from "../../../../../../src/entity/StripeEntitlement";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshTextInput } from "../base_components/sesh_text_input";
import { WithDropdown } from "../base_components/with_dropdown";
import { DiscordEntitlement } from "../premium_page/entitlements/discord_entitlement";
import { StripeEntitlementNoModifyButton } from "../premium_page/entitlements/stripe_entitlement";
import { InfoTooltip } from "../tooltips/info_tooltip";

const AdminViews = {
	Guild: { query: trpc.admin.getGuild.useQuery, element: undefined },
	User: {
		query: trpc.admin.getUser.useQuery,
		element: (id: string) => <UserView id={id} />,
	},
	Event: { query: trpc.admin.getEvent.useQuery, element: undefined },
	Poll: { query: trpc.admin.getPoll.useQuery, element: undefined },
	Sync: {
		query: trpc.admin.getSync.useQuery,
		element: undefined,
	},
} as const;

type QueryType = keyof typeof AdminViews;

export function AdminEntityViewer() {
	const [id_input, setIdInput] = useState<Record<QueryType, string>>({
		Event: "",
		Guild: "",
		Poll: "",
		User: "",
		Sync: "",
	});
	const [id, setId] = useState<Record<QueryType, string>>({
		Event: "",
		Guild: "",
		Poll: "",
		User: "",
		Sync: "",
	});
	const [tab, setTab] = useState<QueryType>("Guild");

	return (
		<div>
			<Tabs
				tab={tab}
				setTab={(tab) => {
					setTab(tab);
				}}
			/>
			<form
				className="w-full"
				onSubmit={(e) => {
					e.preventDefault();
					setId({
						...id,
						[tab]: id_input[tab],
					});
				}}
			>
				<label className="label">{tab} ID</label>
				<div className="flex gap-2">
					<SeshTextInput
						input_props={{
							value: id_input[tab],
							onChange: (e) =>
								setIdInput({
									...id_input,
									[tab]: e.currentTarget.value,
								}),
						}}
					/>
					<SeshPrimaryButton
						className="w-fit rounded-md"
						type="submit"
					>
						<i className="fas fa-search"></i>
					</SeshPrimaryButton>
				</div>
			</form>
			{AdminViews[tab].element && AdminViews[tab].element(id[tab])}
			{id[tab] && <EntityViewer type={tab} id={id[tab]} />}
		</div>
	);
}

function Tabs(props: { tab: QueryType; setTab: (new_tab: QueryType) => void }) {
	const tabs: JSX.Element[] = [];

	for (const key in AdminViews) {
		tabs.push(
			<li
				key={key}
				className={classNames({
					"is-active bg-base-dark": props.tab === key,
				})}
				onClick={() => props.setTab(key as QueryType)}
			>
				<a className="manual-link !bg-inherit">{key}</a>
			</li>
		);
	}

	return (
		<div className="tabs is-boxed">
			<ul>{tabs}</ul>
		</div>
	);
}

function EntityViewer(props: { type: QueryType; id: string }) {
	const query = AdminViews[props.type].query({
		id: props.id,
	});

	if (!query.isSuccess) {
		return <LoadingThrobber />;
	}

	if (!query.data) {
		return <>Not found</>;
	}

	return <JsonViewer value={query.data} theme="light" />;
}

function UserView(props: { id: string }) {
	return <UserEntitlementsTable id={props.id} />;
}

function UserEntitlementsTable(props: { id: string }) {
	const user_query = trpc.admin.getUser.useQuery({
		id: props.id,
	});

	if (!props.id) {
		return <></>;
	}

	if (!user_query.data) {
		return <></>;
	}
	return (
		<div className="mb-24 flex flex-col gap-2">
			<div className="text-xl font-bold">Stripe Entitlements</div>
			<div className="flex flex-col gap-2">
				{user_query.data.stripe_entitlements.map((val, index) => (
					<StripeAdminEntitlement
						key={val.id}
						entity={val}
						user_id={props.id}
						customer_id={user_query.data.stripe_customer_id}
					/>
				))}
			</div>
			<div className="w-fit">
				<AddDropdownButton user_id={props.id} />
			</div>
			<div className="text-xl font-bold">Discord Entitlements</div>
			<div className="flex flex-col gap-2">
				{user_query.data.discord_entitlements.map((val, index) => (
					<DiscordAdminEntitlement
						key={val.id}
						entity={val}
						user_id={props.id}
					/>
				))}
			</div>
		</div>
	);
}

function AddDropdownButton(props: { user_id: string }) {
	const tiers = getTiersWithoutPrices();

	const mutation = trpc.admin.addStripeEntitlement.useMutation({
		onSuccess: () =>
			context.admin.getUser.invalidate({
				id: props.user_id,
			}),
	});
	const context = trpc.useContext();

	const items: JSX.Element[] = [];
	if (!mutation.isLoading) {
		for (const product_id in tiers) {
			const product = getRawProductWithoutPrices(product_id);
			items.push(
				<div
					className="flex cursor-pointer gap-2 p-1 hover:bg-base-dark"
					onClick={() => {
						mutation.mutate({
							product_id: product_id,
							user_id: props.user_id,
						});
					}}
				>
					{product.title} ({product.activations})
				</div>
			);
		}
	} else {
		items.push(<>Loading...</>);
	}

	return (
		<WithDropdown items={items}>
			<div className="w-fit self-center font-bold">Add Entitlement</div>
		</WithDropdown>
	);
}

function StripeAdminEntitlement(props: {
	entity: Omit<StripeEntitlementEntity, "getNumberOfActivations">;
	user_id: string;
	customer_id: string;
}) {
	const context = trpc.useContext();

	const { entity, customer_id } = props;

	const delete_entitlement_mutation =
		trpc.admin.removeStripeEntitlement.useMutation({
			onSuccess: () => {
				context.admin.getUser.invalidate({
					id: props.user_id,
				});
			},
		});

	const [is_deleting_entitlement, setDeletingEntitlement] = useState(false);

	return (
		<div className="flex w-full items-center gap-2">
			<div className="w-full">
				<StripeEntitlementNoModifyButton
					activations={
						getRawProductWithoutPrices(entity.product_id)
							.activations
					}
					{...entity}
				/>
			</div>
			<SeshPrimaryButton
				className="flex w-fit items-center gap-2"
				is_loading={is_deleting_entitlement}
				onClick={() => {
					setDeletingEntitlement(true);
					delete_entitlement_mutation.mutate({
						entitlement_id: entity.id,
					});
				}}
			>
				Delete{" "}
			</SeshPrimaryButton>
			<InfoTooltip
				info={"Will only delete from sesh DB, not from Stripe's end"}
			></InfoTooltip>
		</div>
	);
}

function DiscordAdminEntitlement(props: {
	entity: DiscordEntitlementEntity;
	user_id: string;
}) {
	const context = trpc.useContext();

	const { entity } = props;

	const delete_entitlement_mutation =
		trpc.admin.removeDiscordEntitlement.useMutation({
			onSuccess: () => {
				context.admin.getUser.invalidate({
					id: props.user_id,
				});
			},
		});

	const [is_deleting_entitlement, setDeletingEntitlement] = useState(false);

	return (
		<div className="flex w-full items-center gap-2">
			<div className="w-full">
				<DiscordEntitlement {...entity} />
			</div>
			<SeshPrimaryButton
				className="flex w-fit items-center gap-2"
				is_loading={is_deleting_entitlement}
				onClick={() => {
					setDeletingEntitlement(true);
					delete_entitlement_mutation.mutate({
						entitlement_id: entity.id,
					});
				}}
			>
				Delete{" "}
			</SeshPrimaryButton>
			<InfoTooltip
				info={"Will only delete from sesh DB, not from Stripe's end"}
			></InfoTooltip>
		</div>
	);
}
